import { useQuery } from '@apollo/client';
import React from 'react';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import { isMSTeams } from 'utils/MSTeams/MSTeamsHelpers';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import ChatbotBanner from '../ChatbotBanner/ChatbotBanner';
import Header from '../Header/Header';
import HomeSectionSkeleton from '../components/HomeSectionSkeleton';
import { useSectionErrorLogging } from '../hooks/useSectionErrorLogging';
import { HOME_SECTION_QUERY_OPTIONS } from '../sectionQueryOptions';
import { HomeHero } from './__generated__/HomeHero';
import { HOME_HERO_QUERY } from './heroQueries';

/**
 * Top of the homepage: the Nova chat input for entitled users, otherwise the
 * personalised greeting header.
 */
const Hero = () => {
  const { entitlements, loading: isEntitlementsLoading } =
    useUserEntitlements();

  const { data, loading, error } = useQuery<HomeHero>(
    HOME_HERO_QUERY,
    HOME_SECTION_QUERY_OPTIONS,
  );
  const isHomepageOnboardingTodoListEnabled = useFeatureFlag(
    FEATURE_FLAGS.HOMEPAGE_ONBOARDING_TODOLIST_ENABLED,
  );

  useSectionErrorLogging(error, data, 'hero');

  // `!data`, not bare `loading`: a background refetch sets `loading` while
  // cached data is on screen, which would flash a skeleton over the hero.
  if ((loading && !data) || isEntitlementsLoading) {
    return <HomeSectionSkeleton testId="hero-skeleton" tileCount={1} />;
  }

  // No error branch, unlike every other section: `firstName` is decorative and
  // `useTitle` falls back to a generic greeting, so degrading to "no first
  // name" beats replacing the top of the page with a "Try again" box. The
  // failure is logged above and otherwise ignored.
  const firstName = data?.user?.firstName;

  if (entitlements.nova && !isHomepageOnboardingTodoListEnabled) {
    return <ChatbotBanner firstName={firstName} />;
  }

  return (
    <div
      className={`home-screen-heading${isMSTeams() ? ' is-teams' : ''}${isHomepageOnboardingTodoListEnabled ? ' left-align' : ''}`}
    >
      <Header firstName={firstName} />
    </div>
  );
};

export default Hero;
